/**
 * Sisumail Antenna - Mobile PWA
 * Replicates the browser extension's functionality as an installable app.
 */

import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
// Screens
// Static imports replaced with lazy loading for code splitting
/**
 * Sisumail Antenna - Mobile PWA
 * Replicates the browser extension's functionality as an installable app.
 */
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Toaster } from 'sonner';
import { APP_STATE_KEY, useAppState, useRemoveAccount, useSetAppState } from './hooks/useAppState';
import { initApiHandshake } from './lib/api';
import { logger } from './lib/logger';
// import { type AppState } from './lib/storage'; // Removed direct storage import where possible
import { connectStream, disconnectStream } from './lib/stream';

const InstallScreen = React.lazy(() => import('./screens/InstallScreen'));
const SetupScreen = React.lazy(() => import('./screens/SetupScreen'));
const IdleScreen = React.lazy(() => import('./screens/IdleScreen'));
const ListeningScreen = React.lazy(() => import('./screens/ListeningScreen'));

import { UpdatePrompt } from './components/UpdatePrompt';

declare global {
    interface Navigator {
        standalone?: boolean;
    }
}

const LISTENING_DURATION_MS = 7 * 60 * 1000; // 7 minutes

type View = 'install' | 'setup' | 'idle' | 'listening' | 'add_account';

export default function App() {
    const [view, setView] = useState<View>('install');
    const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
    const queryClient = useQueryClient();

    const { data: state, isLoading: loading } = useAppState();
    const { mutateAsync: setAppState } = useSetAppState();
    const { mutateAsync: removeAccount } = useRemoveAccount();

    const invalidateState = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: APP_STATE_KEY });
    }, [queryClient]);

    // Handle expiry check
    useEffect(() => {
        if (state?.status === 'listening' && state.listeningExpiresAt) {
            const remaining = state.listeningExpiresAt - Date.now();
            if (remaining <= 0) {
                setAppState({ status: 'idle', connectionStatus: 'disconnected' });
            } else {
                setTimeRemaining(remaining);
            }
        }
    }, [state?.status, state?.listeningExpiresAt, setAppState]);

    // =============================================================================
    // Global Actions
    // =============================================================================

    const startListening = useCallback(async () => {
        if (!state?.activeAccountEmail) return;

        const expiresAt = Date.now() + LISTENING_DURATION_MS;
        await setAppState({
            status: 'listening',
            listeningExpiresAt: expiresAt,
        });
        setView('listening');
        setTimeRemaining(LISTENING_DURATION_MS);
        if (navigator.vibrate) navigator.vibrate(50);

        connectStream({
            onSnapshot: () => invalidateState(),
            onSignal: () => invalidateState(),
            onConnectionChange: (status) => {
                setAppState({ connectionStatus: status });
            },
        });
    }, [state?.activeAccountEmail, setAppState, invalidateState]);

    const stopListening = useCallback(async () => {
        disconnectStream();
        await setAppState({
            status: 'idle',
            connectionStatus: 'disconnected',
            listeningExpiresAt: undefined,
        });
        setView('idle');
    }, [setAppState]);

    // Initial Load & Routing
    useEffect(() => {
        if (loading || !state) return;

        const isInstalled =
            window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

        // Check for App Shortcut action
        const params = new URLSearchParams(window.location.search);
        const action = params.get('action');

        // Routing Logic
        if (!isInstalled && view === 'install') {
            // Keep install view
        } else {
            if (state.accounts.length === 0) {
                setView('setup');
            } else if (action === 'listen' && state.status !== 'listening') {
                // Auto-start listening from shortcut
                setView('idle');
                setTimeout(() => startListening(), 100);
            } else if (state.status === 'listening') {
                setView('listening');
            } else if (view !== 'setup' && view !== 'add_account') {
                setView('idle');
            }
        }

        // Clean URL after handling action
        if (action) {
            window.history.replaceState({}, '', '/');
        }
    }, [loading, state, startListening, view]); // Simplified dependencies

    // Timer Interval
    useEffect(() => {
        if (!state || state.status !== 'listening' || timeRemaining === null) return;

        const interval = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev === null || prev <= 0) {
                    clearInterval(interval);
                    stopListening().then(() => logger.info('Listening timed out'));
                    return 0;
                }
                return prev - 1000;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [state?.status, stopListening, state, timeRemaining]);

    // Stream Reconnection (Hot Reload / Re-mount)
    useEffect(() => {
        // Only reconnect if we are supposed to be listening but disconnected
        if (state && state.status === 'listening' && state.connectionStatus === 'disconnected') {
            connectStream({
                onSnapshot: () => invalidateState(),
                onSignal: (_msg) => {
                    invalidateState();
                    if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
                },
                onConnectionChange: (status) => {
                    setAppState({ connectionStatus: status });
                },
            });
        }
    }, [state, invalidateState, setAppState]);

    // =============================================================================
    // Routing render
    // =============================================================================

    if (loading)
        return (
            <div className="app flex items-center justify-center bg-neutral-950" data-testid="app-loading">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
            </div>
        );

    return (
        <>
            <Suspense
                fallback={
                    <div className="app center">
                        <div className="spinner" data-testid="spinner" />
                    </div>
                }
            >
                {renderView()}
            </Suspense>
            <UpdatePrompt />
        </>
    );

    function renderView() {
        switch (view) {
            case 'install':
                return <InstallScreen onContinue={() => setView('setup')} />;

            case 'setup':
            case 'add_account':
                return (
                    <SetupScreen
                        onSuccess={async () => {
                            invalidateState();
                            setView('idle');
                        }}
                    />
                );

            case 'idle':
                if (!state)
                    return (
                        <div className="app center">
                            <div className="spinner" />
                        </div>
                    );
                return (
                    <IdleScreen
                        appState={state}
                        onStartListening={startListening}
                        onAddAccount={() => setView('add_account')}
                        onRemoveAccount={async (email) => {
                            await removeAccount(email);
                        }}
                    />
                );

            case 'listening':
                // Render with partial state or spinner if messages not ready
                if (!state)
                    return (
                        <div className="app center">
                            <div className="spinner" />
                        </div>
                    );

                return (
                    <ListeningScreen
                        appState={state}
                        timeRemaining={timeRemaining}
                        LISTENING_DURATION_MS={LISTENING_DURATION_MS}
                        onStop={stopListening}
                        onRemoveAccount={async (email) => {
                            await removeAccount(email);
                        }}
                    />
                );

            default:
                return <div>Unknown view</div>;
        }
    }
}
